import { useState, useEffect, useRef, useCallback } from "react";
import { io as socketIO } from "socket.io-client";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  ArrowLeft, Package, Clock, CheckCircle2, XCircle,
  ChevronRight, MapPin, Phone, Star, Bike, UtensilsCrossed,
  RefreshCw, Search, Receipt, ThumbsUp, AlertCircle,
  Loader2, X, Zap, RotateCcw, Navigation, Home as HomeIcon,
  ShoppingBag, PackageCheck, CheckCircle,
} from "lucide-react";

// ── Theme ─────────────────────────────────────────
const T = {
  primary:      "#f97316",
  primaryLight: "#fff7ed",
  primaryMid:   "#fed7aa",
  primaryDark:  "#ea580c",
  bg:           "#f2f2f7",
  white:        "#ffffff",
  text:         "#1c1c1e",
  textSub:      "#6b7280",
  textMuted:    "#9ca3af",
  border:       "#e5e7eb",
  green:        "#16a34a",
  greenLight:   "#dcfce7",
  greenMid:     "#86efac",
  red:          "#ef4444",
  redLight:     "#fef2f2",
  yellow:       "#fbbf24",
  yellowLight:  "#fefce8",
  blue:         "#2563eb",
  blueLight:    "#eff6ff",
  purple:       "#7c3aed",
  purpleLight:  "#f5f3ff",
};

const BASE = typeof window !== "undefined" && window.location.hostname !== "localhost"
  ? "https://quickry-ivrv.onrender.com/api"
  : "http://localhost:5000/api";

// ── Order status → step index ─────────────────────
const STATUS_STEP = {
  placed:       0,
  pending:      0,
  accepted:     1,
  preparing:    2,
  packing:      2,  // ✅ grocery
  ready:        2,
  packed:       2,  // ✅ grocery
  picked:       3,
  picked_up:    3,  // ✅ FIX: picked_up bhi step 3 pe
  "on-the-way": 3,
  delivered:    4,
};

// ── Timeline steps ────────────────────────────────
const getSteps = (isGrocery) => [
  { label: "Order Placed",                         icon: Receipt,       color: T.blue    },
  { label: "Order Confirmed",                      icon: CheckCircle2,  color: T.green   },
  { label: isGrocery ? "Packing in Progress" : "Preparing Your Food", icon: isGrocery ? PackageCheck : UtensilsCrossed, color: T.primary },
  { label: "Rider Picked Up",                      icon: Bike,          color: T.purple  },
  { label: "Delivered",                            icon: HomeIcon,      color: T.green   },
];

// ── Step description ──────────────────────────────
const getStepDesc = (step, order, isGrocery) => {
  const riderName = order?.rider?.name || "Your rider";
  switch(step) {
    case 0: return "Waiting for the restaurant to confirm your order...";
    case 1: return isGrocery ? "The store has confirmed your order." : "The restaurant has accepted your order.";
    case 2: return isGrocery ? "Your items are being packed carefully." : "The chef is preparing your food 👨‍🍳";
    case 3: return `${riderName} has picked up your order and is heading your way!`;
    case 4: return "Your order has been delivered. Enjoy! 🎉";
    default: return "";
  }
};

// ── Helpers ───────────────────────────────────────
function useWindowWidth() {
  const [w, setW] = useState(typeof window !== "undefined" ? window.innerWidth : 390);
  useEffect(() => {
    const h = () => setW(window.innerWidth);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);
  return w;
}

function Card({ children, style = {} }) {
  return (
    <div style={{ background: T.white, borderRadius: 16, border: `1px solid ${T.border}`, overflow: "hidden", ...style }}>
      {children}
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    live:      { label: "Live",      bg: T.primaryLight, color: T.primaryDark },
    delivered: { label: "Delivered", bg: T.greenLight,   color: T.green       },
    cancelled: { label: "Cancelled", bg: T.redLight,     color: T.red         },
  };
  const s = map[status] || map.live;
  return (
    <div style={{ display: "inline-flex", alignItems: "center", gap: 5, background: s.bg, color: s.color, fontSize: "0.68rem", fontWeight: 700, padding: "3px 9px", borderRadius: 100 }}>
      <div style={{ width: 6, height: 6, borderRadius: "50%", background: s.color, ...(status === "live" ? { animation: "blink 1.2s ease-in-out infinite" } : {}) }} />
      {s.label}
    </div>
  );
}

function StarRating({ value, onChange, size = 24 }) {
  const [hovered, setHovered] = useState(0);
  return (
    <div style={{ display: "flex", gap: 4 }}>
      {[1, 2, 3, 4, 5].map(n => (
        <Star
          key={n}
          size={size}
          fill={(hovered || value) >= n ? T.yellow : "none"}
          color={(hovered || value) >= n ? T.yellow : T.border}
          strokeWidth={1.5}
          style={{ cursor: onChange ? "pointer" : "default", transition: "all .1s" }}
          onMouseEnter={() => onChange && setHovered(n)}
          onMouseLeave={() => onChange && setHovered(0)}
          onClick={() => onChange?.(n)}
        />
      ))}
    </div>
  );
}


// ── Live Map Component (Leaflet + Blue Route) ─────
function RiderMap({ riderLocation, customerLocation }) {
  const mapRef    = useRef(null);
  const mapObj    = useRef(null);
  const markerRef = useRef(null);
  const destRef   = useRef(null);
  const routeRef  = useRef(null);

  const initMap = (L, rLat, rLng) => {
    if (!mapRef.current || mapObj.current) return;

    const map = L.map(mapRef.current, { zoomControl: false, attributionControl: false })
      .setView([rLat, rLng], 14);

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png").addTo(map);

    // 🛵 Rider marker — orange
    const riderIcon = L.divIcon({
      html: `<div style="width:42px;height:42px;background:#f97316;border:3px solid #fff;border-radius:50%;display:flex;align-items:center;justify-content:center;box-shadow:0 4px 14px rgba(249,115,22,0.6);font-size:20px;">🛵</div>`,
      className: "", iconSize: [42, 42], iconAnchor: [21, 21],
    });
    markerRef.current = L.marker([rLat, rLng], { icon: riderIcon }).addTo(map);

    // 🏠 Customer marker — blue (if location available)
    if (customerLocation?.lat && customerLocation?.lng) {
      const custIcon = L.divIcon({
        html: `<div style="width:38px;height:38px;background:#2563eb;border:3px solid #fff;border-radius:50%;display:flex;align-items:center;justify-content:center;box-shadow:0 4px 14px rgba(37,99,235,0.5);font-size:18px;">🏠</div>`,
        className: "", iconSize: [38, 38], iconAnchor: [19, 19],
      });
      destRef.current = L.marker([customerLocation.lat, customerLocation.lng], { icon: custIcon }).addTo(map);

      // ── Blue route line via OSRM (free routing) ──
      fetch(`https://router.project-osrm.org/route/v1/driving/${rLng},${rLat};${customerLocation.lng},${customerLocation.lat}?overview=full&geometries=geojson`)
        .then(r => r.json())
        .then(data => {
          if (data.routes?.[0]?.geometry) {
            if (routeRef.current) map.removeLayer(routeRef.current);
            routeRef.current = L.geoJSON(data.routes[0].geometry, {
              style: { color: "#2563eb", weight: 4, opacity: 0.85, dashArray: "8,4" }
            }).addTo(map);
            // Fit map to show both markers
            const bounds = L.latLngBounds(
              [rLat, rLng],
              [customerLocation.lat, customerLocation.lng]
            );
            map.fitBounds(bounds, { padding: [40, 40] });
          }
        })
        .catch(() => {
          // Fallback: straight line if OSRM fails
          routeRef.current = L.polyline(
            [[rLat, rLng], [customerLocation.lat, customerLocation.lng]],
            { color: "#2563eb", weight: 3, opacity: 0.7, dashArray: "8,4" }
          ).addTo(map);
        });
    }

    mapObj.current = map;
  };

  useEffect(() => {
    if (!riderLocation) return;
    const { lat: rLat, lng: rLng } = riderLocation;

    if (!document.getElementById("leaflet-css")) {
      const link = document.createElement("link");
      link.id = "leaflet-css"; link.rel = "stylesheet";
      link.href = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
      document.head.appendChild(link);
    }

    if (window.L) {
      initMap(window.L, rLat, rLng);
    } else {
      const script = document.createElement("script");
      script.src = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js";
      script.onload = () => initMap(window.L, rLat, rLng);
      document.head.appendChild(script);
    }
  }, [!!riderLocation]);

  // Update rider marker position live
  useEffect(() => {
    if (!mapObj.current || !markerRef.current || !riderLocation) return;
    const { lat, lng } = riderLocation;
    markerRef.current.setLatLng([lat, lng]);

    // Update route line
    if (customerLocation?.lat && routeRef.current) {
      fetch(`https://router.project-osrm.org/route/v1/driving/${lng},${lat};${customerLocation.lng},${customerLocation.lat}?overview=full&geometries=geojson`)
        .then(r => r.json())
        .then(data => {
          if (data.routes?.[0]?.geometry && routeRef.current) {
            mapObj.current.removeLayer(routeRef.current);
            routeRef.current = window.L.geoJSON(data.routes[0].geometry, {
              style: { color: "#2563eb", weight: 4, opacity: 0.85, dashArray: "8,4" }
            }).addTo(mapObj.current);
          }
        }).catch(() => {});
    }
  }, [riderLocation?.lat, riderLocation?.lng]);

  if (!riderLocation) return null;

  return (
    <div style={{ margin: "0 16px 14px", borderRadius: 14, overflow: "hidden", border: `1px solid ${T.border}`, position: "relative" }}>
      <div ref={mapRef} style={{ height: 240, width: "100%" }} />
      <div style={{ position: "absolute", bottom: 10, left: 10, background: "rgba(249,115,22,0.92)", color: "#fff", fontSize: "0.68rem", fontWeight: 700, padding: "4px 10px", borderRadius: 100, backdropFilter: "blur(4px)" }}>
        🛵 Rider is on the way!
      </div>
      {customerLocation?.lat && (
        <div style={{ position: "absolute", bottom: 10, right: 10, background: "rgba(37,99,235,0.92)", color: "#fff", fontSize: "0.68rem", fontWeight: 700, padding: "4px 10px", borderRadius: 100, backdropFilter: "blur(4px)" }}>
          🏠 Your location
        </div>
      )}
    </div>
  );
}

// ── Live Tracking Card — polls backend every 10s ──
function LiveTrackingCard({ orderId, initialOrder, token }) {
  const [order,         setOrder]         = useState(initialOrder);
  const [loading,       setLoading]       = useState(false);
  const [riderLocation, setRiderLocation] = useState(null);
  const intervalRef = useRef(null);
  const socketRef   = useRef(null);

  // Socket.io — live rider location
  useEffect(() => {
    // Always use api.quickry.in — backend is on server
    const SOCKET_URL = "https://api.quickry.in";

    const socket = socketIO(SOCKET_URL, { 
      transports: ["websocket", "polling"],
      reconnection: true,
      reconnectionAttempts: 10,
    });
    socketRef.current = socket;

    socket.on("connect", () => {
      console.log("📍 Customer tracking socket connected");
      socket.emit("customer:track", { orderId });
    });

    socket.on("rider:location", (data) => {
      if (data.orderId === orderId && data.lat && data.lng) {
        setRiderLocation({ lat: data.lat, lng: data.lng });
      }
    });

    socket.on("reconnect", () => {
      socket.emit("customer:track", { orderId });
    });

    return () => { socket.disconnect(); socketRef.current = null; };
  }, [orderId, token]);

  const isGrocery = order?.orderType === "grocery" || order?.serviceType === "grocery"; // ✅ FIX: both field names
  const STEPS     = getSteps(isGrocery);
  const step      = STATUS_STEP[order?.status] ?? 0;

  const fetchTracking = useCallback(async () => {
    try {
      const res  = await fetch(`${BASE}/orders/track/${orderId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) setOrder(data.order);
    } catch {}
  }, [orderId, token]);

  // Poll every 10 seconds for live orders
  useEffect(() => {
    if (order?.status === "delivered" || order?.status === "cancelled") return;
    fetchTracking();
    intervalRef.current = setInterval(fetchTracking, 10000);
    return () => clearInterval(intervalRef.current);
  }, [fetchTracking, order?.status]);

  const currentStep = STEPS[step];
  const StepIcon    = currentStep.icon;

  return (
    <Card style={{ marginBottom: 12, border: `1.5px solid ${T.primaryMid}`, boxShadow: "0 4px 20px rgba(249,115,22,0.10)" }}>

      {/* Header */}
      <div style={{ background: `linear-gradient(135deg,${T.primaryDark},${T.primary})`, padding: "14px 16px" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#fff", animation: "blink 1.2s ease-in-out infinite" }} />
            <span style={{ color: "#fff", fontWeight: 800, fontSize: "0.85rem" }}>LIVE TRACKING</span>
          </div>
          <button onClick={fetchTracking} style={{ background: "rgba(255,255,255,0.15)", border: "none", borderRadius: 8, padding: "4px 10px", display: "flex", alignItems: "center", gap: 5, cursor: "pointer" }}>
            <RefreshCw size={12} color="#fff"/>
            <span style={{ color: "#fff", fontSize: "0.72rem", fontWeight: 600 }}>Refresh</span>
          </button>
        </div>
        <div style={{ color: "#fff", fontFamily: "'Fraunces',Georgia,serif", fontWeight: 900, fontSize: "1.1rem", letterSpacing: "-0.5px" }}>
          {order?.restaurant?.name || order?.groceryStore?.name || order?.store?.name || order?.restaurantName || (isGrocery ? "Grocery Store" : "Restaurant")}
        </div>
        <div style={{ color: "rgba(255,255,255,0.75)", fontSize: "0.72rem", marginTop: 2 }}>
          Order #{orderId?.slice(-6).toUpperCase()}
        </div>
      </div>

      {/* Progress Timeline */}
      <div style={{ padding: "16px 16px 10px" }}>
        <div style={{ display: "flex", alignItems: "flex-start", position: "relative" }}>
          {STEPS.map((s, i) => {
            const SIcon   = s.icon;
            const done    = i <= step;
            const current = i === step;
            return (
              <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", position: "relative", zIndex: 1 }}>
                <div style={{
                  width: current ? 36 : 30, height: current ? 36 : 30,
                  borderRadius: "50%",
                  background: done ? s.color : T.border,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  boxShadow: current ? `0 0 0 4px ${s.color}30` : "none",
                  transition: "all .4s",
                  zIndex: 2,
                }}>
                  <SIcon size={current ? 17 : 14} color={done ? "#fff" : T.textMuted} strokeWidth={2} />
                </div>
                {i < 4 && (
                  <div style={{
                    position: "absolute", top: current ? 18 : 15, left: "50%", width: "100%",
                    height: 3, borderRadius: 2,
                    background: i < step ? s.color : T.border,
                    transition: "background .4s", zIndex: 1,
                  }} />
                )}
                <div style={{
                  fontSize: "0.58rem", fontWeight: current ? 700 : 500,
                  color: current ? s.color : done ? T.textSub : T.textMuted,
                  marginTop: 6, textAlign: "center", lineHeight: 1.3, maxWidth: 52,
                }}>
                  {s.label}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Current status description */}
      <div style={{ margin: "0 16px 14px", background: T.primaryLight, border: `1px solid ${T.primaryMid}`, borderRadius: 10, padding: "10px 14px", display: "flex", alignItems: "center", gap: 10 }}>
        <div style={{ width: 32, height: 32, borderRadius: "50%", background: currentStep.color, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <StepIcon size={15} color="#fff" strokeWidth={2} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: "0.82rem", color: T.primaryDark }}>{currentStep.label}</div>
          <div style={{ fontSize: "0.7rem", color: T.textSub, marginTop: 1 }}>
            {getStepDesc(step, order, isGrocery)}
          </div>
        </div>
      </div>

      {/* Rider info — shown when picked up */}
      {order?.rider?.name && step >= 3 && (
        <div style={{ margin: "0 16px 14px", border: `1px solid ${T.border}`, borderRadius: 12, padding: "12px 14px" }}>
          <div style={{ fontSize: "0.65rem", fontWeight: 700, color: T.textMuted, letterSpacing: "1px", textTransform: "uppercase", marginBottom: 10 }}>
            Your Delivery Partner
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ width: 44, height: 44, borderRadius: "50%", background: `linear-gradient(135deg,${T.primaryDark},${T.primary})`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <span style={{ color: "#fff", fontWeight: 800, fontSize: "1rem" }}>
                {order.rider.name.charAt(0).toUpperCase()}
              </span>
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, fontSize: "0.88rem" }}>{order.rider.name}</div>
              <div style={{ fontSize: "0.72rem", color: T.textSub, marginTop: 2 }}>
                {order.rider.vehicleNo || "Delivery Rider"}
              </div>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              {order.rider.phone && (
                <a href={`tel:${order.rider.phone}`} style={{ width: 38, height: 38, borderRadius: "50%", background: T.greenLight, border: `1px solid ${T.greenMid}`, display: "flex", alignItems: "center", justifyContent: "center", textDecoration: "none" }}>
                  <Phone size={16} color={T.green} />
                </a>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Live Map — shown when rider is assigned (step >= 3) */}
      {step >= 3 && riderLocation && (
        <RiderMap
          riderLocation={riderLocation}
          customerLocation={
            order?.deliveryAddress?.lat && order?.deliveryAddress?.lng
              ? { lat: order.deliveryAddress.lat, lng: order.deliveryAddress.lng }
              : order?.deliveryAddress?.latitude && order?.deliveryAddress?.longitude
              ? { lat: order.deliveryAddress.latitude, lng: order.deliveryAddress.longitude }
              : null
          }
        />
      )}
      {step >= 3 && !riderLocation && (
        <div style={{ margin: "0 16px 14px", background: "#f9fafb", borderRadius: 14, padding: "18px", textAlign: "center", border: "1px dashed #e5e7eb" }}>
          <div style={{ fontSize: "1.5rem", marginBottom: 6 }}>🛵</div>
          <div style={{ fontSize: "0.78rem", fontWeight: 600, color: "#6b7280" }}>Fetching rider location...</div>
          <div style={{ fontSize: "0.68rem", color: "#9ca3af", marginTop: 3 }}>Map will appear when rider is moving</div>
        </div>
      )}

      {/* Delivery address */}
      {order?.deliveryAddress && (
        <div style={{ margin: "0 16px 14px", display: "flex", alignItems: "flex-start", gap: 10 }}>
          <MapPin size={14} color={T.textMuted} style={{ flexShrink: 0, marginTop: 2 }} />
          <div style={{ fontSize: "0.75rem", color: T.textSub, lineHeight: 1.5 }}>
            <span style={{ fontWeight: 600, color: T.text }}>Delivering to: </span>
            {order.deliveryAddress.address || order.deliveryAddress.street || order.deliveryAddress.fullAddress || "Your address"}
          </div>
        </div>
      )}

      {/* Full timeline */}
      <div style={{ margin: "0 16px 16px" }}>
        <div style={{ fontSize: "0.65rem", fontWeight: 700, color: T.textMuted, letterSpacing: "1px", textTransform: "uppercase", marginBottom: 10 }}>
          Order Timeline
        </div>
        {[
          { label: "Order placed",        time: order?.createdAt,    show: true },
          { label: "Restaurant accepted", time: order?.acceptedAt,   show: !!order?.acceptedAt },
          { label: isGrocery ? "Packing started" : "Preparation started", time: order?.preparingAt, show: !!order?.preparingAt },
          { label: isGrocery ? "Packed & ready" : "Ready for pickup", time: order?.readyAt, show: !!order?.readyAt },
          { label: "Rider picked up",     time: order?.pickedAt,     show: !!order?.pickedAt },
          { label: "Out for delivery",    time: order?.onTheWayAt,   show: !!order?.onTheWayAt },
          { label: "Delivered",           time: order?.deliveredAt,  show: !!order?.deliveredAt },
        ].filter(e => e.show).map((e, i, arr) => (
          <div key={i} style={{ display: "flex", gap: 12, marginBottom: i < arr.length - 1 ? 10 : 0 }}>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
              <div style={{ width: 8, height: 8, borderRadius: "50%", background: T.primary, flexShrink: 0, marginTop: 4 }} />
              {i < arr.length - 1 && <div style={{ width: 2, flex: 1, background: T.border, marginTop: 4 }} />}
            </div>
            <div style={{ paddingBottom: i < arr.length - 1 ? 10 : 0 }}>
              <div style={{ fontSize: "0.78rem", fontWeight: 600, color: T.text }}>{e.label}</div>
              {e.time && (
                <div style={{ fontSize: "0.68rem", color: T.textMuted, marginTop: 2 }}>
                  {new Date(e.time).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

// ── Order Detail Sheet ────────────────────────────
function OrderDetailSheet({ order, onClose, isMobile, token }) {
  const [rating,    setRating]   = useState(order.rating || 0);
  const [submitted, setSubmit]   = useState(!!order.rating);
  const [submitting, setSubm]    = useState(false);

  const handleRateSubmit = async () => {
    if (!rating) return;
    setSubm(true);
    try {
      await fetch(`${BASE}/orders/${order.id}/rate`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ rating }),
      });
    } catch {}
    setSubm(false);
    setSubmit(true);
  };

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 300, display: "flex", alignItems: "flex-end" }}>
      <div onClick={e => e.stopPropagation()} style={{ background: T.white, borderRadius: "20px 20px 0 0", width: "100%", maxWidth: 560, margin: "0 auto", maxHeight: "88vh", overflowY: "auto", paddingBottom: 36 }}>

        {/* Handle */}
        <div style={{ position: "sticky", top: 0, background: T.white, padding: "14px 16px 10px", zIndex: 10, borderBottom: `1px solid ${T.border}` }}>
          <div style={{ width: 32, height: 4, borderRadius: 100, background: T.border, margin: "0 auto 12px" }} />
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div style={{ fontFamily: "'Fraunces',Georgia,serif", fontWeight: 900, fontSize: "1.05rem", letterSpacing: "-0.5px" }}>Order Details</div>
            <button onClick={onClose} style={{ background: T.bg, border: "none", borderRadius: "50%", width: 32, height: 32, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
              <X size={16} color={T.textSub} />
            </button>
          </div>
        </div>

        <div style={{ padding: "16px 16px 0" }}>
          {/* Restaurant */}
          <div style={{ display: "flex", alignItems: "flex-start", gap: 12, marginBottom: 16 }}>
            <div style={{ width: 48, height: 48, borderRadius: 14, background: "linear-gradient(135deg,#ea580c,#f97316)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <UtensilsCrossed size={22} color="rgba(255,255,255,0.85)" strokeWidth={1.5} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 800, fontSize: "0.97rem", marginBottom: 4 }}>{order.restaurant}</div>
              <StatusBadge status={order.status} />
            </div>
          </div>

          {/* Meta */}
          <div style={{ background: T.bg, borderRadius: 12, padding: "12px 14px", marginBottom: 14, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            {[
              { label: "Order ID",  value: `#${order.id?.slice(-6).toUpperCase()}` },
              { label: "Placed At", value: order.placedAt },
              { label: "Payment",   value: order.paymentMode },
              { label: order.status === "delivered" ? "Delivered At" : "Status",
                value: order.deliveredAt
                  ? new Date(order.deliveredAt).toLocaleString("en-IN", { day:"numeric", month:"short", hour:"2-digit", minute:"2-digit" })
                  : order.status === "cancelled" ? "Cancelled" : "In Progress" },
            ].map((m, i) => (
              <div key={i}>
                <div style={{ fontSize: "0.62rem", fontWeight: 700, color: T.textMuted, textTransform: "uppercase", letterSpacing: "1px", marginBottom: 3 }}>{m.label}</div>
                <div style={{ fontWeight: 600, fontSize: "0.8rem", color: T.text }}>{m.value}</div>
              </div>
            ))}
          </div>

          {/* Items */}
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: "0.65rem", fontWeight: 700, color: T.textMuted, letterSpacing: "1.5px", textTransform: "uppercase", marginBottom: 10 }}>
              Items Ordered
            </div>
            {order.items.map((item, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 0", borderBottom: i < order.items.length - 1 ? `1px dashed ${T.border}` : "none" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{ width: 20, height: 20, borderRadius: "50%", background: T.primaryLight, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "0.7rem", fontWeight: 800, color: T.primary, flexShrink: 0 }}>
                    {item.qty}x
                  </div>
                  <span style={{ fontSize: "0.85rem", fontWeight: 500, color: T.text }}>{item.name}</span>
                </div>
                <span style={{ fontWeight: 700, fontSize: "0.85rem" }}>₹{item.price * item.qty}</span>
              </div>
            ))}
          </div>

          {/* Bill */}
          <div style={{ background: T.bg, borderRadius: 12, padding: "13px 14px", marginBottom: 14 }}>
            <div style={{ fontSize: "0.65rem", fontWeight: 700, color: T.textMuted, letterSpacing: "1.5px", textTransform: "uppercase", marginBottom: 10 }}>
              Bill Summary
            </div>
            {[
              { l: "Item Total",   v: `₹${order.items.reduce((s, i) => s + i.price * i.qty, 0)}`, green: false },
              { l: "Delivery Fee", v: order.deliveryFee === 0 ? "FREE" : `₹${order.deliveryFee}`, green: order.deliveryFee === 0 },
              ...(order.discount > 0 ? [{ l: "Discount", v: `-₹${order.discount}`, green: true }] : []),
            ].map((r, i) => (
              <div key={i} style={{ display: "flex", justifyContent: "space-between", marginBottom: 7 }}>
                <span style={{ fontSize: "0.8rem", color: T.textSub }}>{r.l}</span>
                <span style={{ fontSize: "0.8rem", fontWeight: 600, color: r.green ? T.green : T.text }}>{r.v}</span>
              </div>
            ))}
            <div style={{ height: 1, background: T.border, margin: "8px 0" }} />
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span style={{ fontWeight: 800, fontSize: "0.9rem" }}>Total Paid</span>
              <span style={{ fontWeight: 800, fontSize: "0.9rem" }}>₹{order.total}</span>
            </div>
          </div>

          {/* Address */}
          <div style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 16, background: T.bg, borderRadius: 12, padding: "11px 14px" }}>
            <MapPin size={15} color={T.primary} style={{ flexShrink: 0, marginTop: 1 }} />
            <div>
              <div style={{ fontSize: "0.65rem", fontWeight: 700, color: T.textMuted, textTransform: "uppercase", letterSpacing: "1px", marginBottom: 3 }}>Delivered To</div>
              <div style={{ fontSize: "0.8rem", color: T.text, lineHeight: 1.5 }}>{order.address}</div>
            </div>
          </div>

          {/* Rate order */}
          {order.status === "delivered" && (
            <div style={{ border: `1.5px solid ${submitted ? T.greenMid : T.primaryMid}`, background: submitted ? T.greenLight : T.primaryLight, borderRadius: 14, padding: "14px", marginBottom: 14 }}>
              {submitted ? (
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <CheckCircle2 size={20} color={T.green} />
                  <div>
                    <div style={{ fontWeight: 700, fontSize: "0.85rem", color: T.green }}>Thanks for your rating!</div>
                    <div style={{ marginTop: 4 }}><StarRating value={rating} size={16} /></div>
                  </div>
                </div>
              ) : (
                <>
                  <div style={{ fontWeight: 700, fontSize: "0.85rem", color: T.primaryDark, marginBottom: 10 }}>Rate your experience</div>
                  <StarRating value={rating} onChange={setRating} size={28} />
                  {rating > 0 && (
                    <button onClick={handleRateSubmit} disabled={submitting} style={{ marginTop: 12, width: "100%", background: T.primary, color: "#fff", border: "none", borderRadius: 10, padding: "10px", fontWeight: 700, fontSize: "0.83rem", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 6, fontFamily: "inherit" }}>
                      {submitting ? <><Loader2 size={14} style={{ animation: "spin .75s linear infinite" }} /> Submitting...</> : <><ThumbsUp size={14} /> Submit Rating</>}
                    </button>
                  )}
                </>
              )}
            </div>
          )}

          {/* Cancelled reason */}
          {order.status === "cancelled" && order.cancelReason && (
            <div style={{ marginBottom: 14, background: T.redLight, border: `1px solid #fecaca`, borderRadius: 12, padding: "11px 14px", display: "flex", alignItems: "flex-start", gap: 8 }}>
              <AlertCircle size={15} color={T.red} style={{ flexShrink: 0, marginTop: 1 }} />
              <div>
                <div style={{ fontWeight: 700, fontSize: "0.8rem", color: T.red, marginBottom: 2 }}>Cancellation Reason</div>
                <div style={{ fontSize: "0.75rem", color: T.red, opacity: 0.85 }}>{order.cancelReason}</div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Order Card (list) ─────────────────────────────
function OrderCard({ order, onClick, isMobile }) {
  return (
    <div
      onClick={onClick}
      style={{ background: T.white, borderRadius: 16, border: `1.5px solid ${order.status === "live" ? T.primaryMid : T.border}`, marginBottom: 10, overflow: "hidden", cursor: "pointer", transition: "transform .15s, box-shadow .15s" }}
      onMouseEnter={e => { if (!isMobile) { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 8px 28px rgba(0,0,0,0.09)"; }}}
      onMouseLeave={e => { e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = "none"; }}
    >
      <div style={{ height: 4, background: "linear-gradient(135deg,#ea580c,#f97316)" }} />
      <div style={{ padding: "13px 14px" }}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 12, marginBottom: 10 }}>
          <div style={{ width: 44, height: 44, borderRadius: 13, background: "linear-gradient(135deg,#ea580c,#f97316)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            {order.serviceType === "grocery" ? <ShoppingBag size={20} color="rgba(255,255,255,0.85)" strokeWidth={1.5} /> : <UtensilsCrossed size={20} color="rgba(255,255,255,0.85)" strokeWidth={1.5} />}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 700, fontSize: "0.92rem", color: T.text, marginBottom: 3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {order.restaurant}
            </div>
            <div style={{ fontSize: "0.72rem", color: T.textSub }}>
              {order.items.slice(0, 2).map(i => i.name).join(", ")}
              {order.items.length > 2 ? ` +${order.items.length - 2} more` : ""}
            </div>
          </div>
          <StatusBadge status={order.status} />
        </div>

        <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 2 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 4, color: T.textSub, fontSize: "0.73rem" }}>
            <Clock size={12} strokeWidth={2} /><span>{order.placedAt}</span>
          </div>
          <div style={{ width: 3, height: 3, borderRadius: "50%", background: T.textMuted, margin: "0 8px" }} />
          <div style={{ fontWeight: 700, fontSize: "0.8rem", color: T.text }}>₹{order.total}</div>
          <div style={{ width: 3, height: 3, borderRadius: "50%", background: T.textMuted, margin: "0 8px" }} />
          <div style={{ fontSize: "0.72rem", color: T.textSub }}>{order.items.length} item{order.items.length > 1 ? "s" : ""}</div>
          {order.status === "live" && (
            <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 4, background: T.primaryLight, color: T.primary, padding: "3px 9px", borderRadius: 100, fontSize: "0.7rem", fontWeight: 700 }}>
              <Zap size={10} fill={T.primary} color={T.primary} /> Live
            </div>
          )}
          {order.rating && (
            <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 3 }}>
              {[...Array(order.rating)].map((_, i) => <Star key={i} size={12} fill={T.yellow} color={T.yellow} />)}
            </div>
          )}
        </div>
      </div>

      <div style={{ borderTop: `1px solid ${T.border}`, padding: "9px 14px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ fontSize: "0.73rem", color: T.primary, fontWeight: 700, display: "flex", alignItems: "center", gap: 4 }}>
          {order.status === "live"      && <><Bike size={13} /> Track Order</>}
          {order.status === "delivered" && <><Receipt size={13} /> View Details</>}
          {order.status === "cancelled" && <><AlertCircle size={13} /> View Details</>}
        </div>
        <ChevronRight size={16} color={T.textMuted} />
      </div>
    </div>
  );
}

// ── Main Orders Page ──────────────────────────────
export default function OrdersPage() {
  const navigate         = useNavigate();
  const { getToken }     = useAuth();
  const token            = getToken?.();

  const [orders,      setOrders]  = useState([]);
  const [loading,     setLoading] = useState(true);
  const [filter,      setFilter]  = useState("all");
  const [search,      setSearch]  = useState("");
  const [detailOrder, setDetail]  = useState(null);

  const width    = useWindowWidth();
  const isMobile = width < 768;
  const px       = isMobile ? 14 : 20;

  const fetchOrders = useCallback(async () => {
    try {
      const res  = await fetch(`${BASE}/orders/my`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) {
        const normalized = (data.orders || []).map(o => ({
          id:           o._id,
          restaurant:   o.restaurant?.name || o.groceryStore?.name || o.store?.name || "Store",
          restaurantPhone: o.restaurant?.phone || o.groceryStore?.phone || o.store?.phone || "",
          items:        (o.items || []).map(i => ({
            name:  i.name || "Item",
            qty:   i.qty || 1,
            price: i.price || 0,
          })),
          total:       o.total || 0,
          status:      ["placed","pending","accepted","preparing","packing","packed","ready","picked","picked_up","on-the-way"].includes(o.status) ? "live" : o.status,
          rawStatus:   o.status,
          serviceType: o.serviceType || (o.groceryStore || o.store ? "grocery" : "food"),
          liveStep:    STATUS_STEP[o.status] ?? 0,
          placedAt:    o.createdAt ? new Date(o.createdAt).toLocaleString("en-IN", { day:"numeric", month:"short", hour:"2-digit", minute:"2-digit" }) : "",
          deliveredAt: o.deliveredAt || null,
          address:     o.deliveryAddress?.address || o.deliveryAddress?.street || "",
          paymentMode: (o.paymentMode || "COD").toUpperCase(),
          deliveryFee: o.deliveryFee || 0,
          discount:    o.discount || 0,
          rider:       o.rider || null,
          rating:      o.rating || null,
          cancelReason:o.cancelReason || "",
          // Keep raw order for tracking
          _raw: o,
        }));
        setOrders(normalized);
      }
    } catch (e) {
      console.error("Orders fetch error:", e);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  // Auto-refresh live orders every 15s
  useEffect(() => {
    const hasLive = orders.some(o => o.status === "live");
    if (!hasLive) return;
    const t = setInterval(fetchOrders, 15000);
    return () => clearInterval(t);
  }, [orders, fetchOrders]);

  const liveOrders     = orders.filter(o => o.status === "live");
  const filteredOrders = orders.filter(o => {
    const matchFilter = filter === "all" || o.status === filter;
    const matchSearch = !search || o.restaurant.toLowerCase().includes(search.toLowerCase()) || o.items.some(i => i.name.toLowerCase().includes(search.toLowerCase()));
    return matchFilter && matchSearch;
  });

  const FILTERS = [
    { id: "all",       label: "All Orders" },
    { id: "live",      label: "Live"       },
    { id: "delivered", label: "Delivered"  },
    { id: "cancelled", label: "Cancelled"  },
  ];

  if (loading) return (
    <div style={{ minHeight: "100vh", background: T.bg, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
      <div style={{ textAlign: "center" }}>
        <Loader2 size={32} color={T.primary} style={{ animation: "spin .75s linear infinite", marginBottom: 12 }} />
        <div style={{ color: T.textSub, fontSize: "0.85rem" }}>Loading your orders...</div>
      </div>
    </div>
  );

  return (
    <div style={{ background: T.bg, minHeight: "100vh", fontFamily: "'Plus Jakarta Sans',sans-serif", color: T.text, paddingBottom: 40 }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,700;9..144,800;9..144,900&family=Plus+Jakarta+Sans:wght@300;400;500;600;700;800&display=swap');
        *, *::before, *::after { box-sizing:border-box; margin:0; padding:0; }
        body { background:${T.bg}; -webkit-tap-highlight-color:transparent; }
        input, button { font-family:inherit; }
        input:focus { outline:none; }
        @keyframes fadeUp { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
        @keyframes spin   { to{transform:rotate(360deg)} }
        @keyframes blink  { 0%,100%{opacity:1} 50%{opacity:0.3} }
        ::-webkit-scrollbar { display:none; }
      `}</style>

      {/* Header */}
      <div style={{ position: "sticky", top: 0, zIndex: 100, background: T.white, borderBottom: `1px solid ${T.border}` }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, padding: `13px ${px + 4}px`, maxWidth: 900, margin: "0 auto" }}>
          <button onClick={() => navigate(-1)} style={{ background: "none", border: "none", cursor: "pointer", color: T.text, display: "flex", padding: 4 }}>
            <ArrowLeft size={21} />
          </button>
          <div style={{ fontFamily: "'Fraunces',Georgia,serif", fontWeight: 900, fontSize: "1.15rem", letterSpacing: "-0.5px", flex: 1 }}>My Orders</div>
          <button onClick={fetchOrders} style={{ background: "none", border: "none", cursor: "pointer", color: T.textSub, display: "flex", padding: 4 }}>
            <RefreshCw size={18} />
          </button>
          {liveOrders.length > 0 && (
            <div style={{ display: "flex", alignItems: "center", gap: 5, background: T.primaryLight, border: `1px solid ${T.primaryMid}`, borderRadius: 100, padding: "4px 10px" }}>
              <div style={{ width: 7, height: 7, borderRadius: "50%", background: T.primary, animation: "blink 1.2s ease-in-out infinite" }} />
              <span style={{ fontSize: "0.7rem", fontWeight: 700, color: T.primary }}>{liveOrders.length} Live</span>
            </div>
          )}
        </div>

        {/* Search */}
        <div style={{ padding: `0 ${px}px 10px`, maxWidth: 900, margin: "0 auto" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 9, background: T.bg, border: `1.5px solid ${T.border}`, borderRadius: 11, padding: "10px 13px" }}>
            <Search size={15} color={T.textMuted} />
            <input
              placeholder="Search orders or restaurants..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ flex: 1, border: "none", background: "transparent", color: T.text, fontSize: "0.85rem" }}
            />
            {search && <button onClick={() => setSearch("")} style={{ background: "none", border: "none", cursor: "pointer", display: "flex", color: T.textMuted }}><X size={14} /></button>}
          </div>
        </div>

        {/* Filter tabs */}
        <div style={{ display: "flex", padding: `0 ${px}px`, borderTop: `1px solid ${T.border}`, overflowX: "auto", scrollbarWidth: "none", maxWidth: 900, margin: "0 auto" }}>
          {FILTERS.map(f => (
            <button key={f.id} onClick={() => setFilter(f.id)} style={{ flexShrink: 0, background: "none", border: "none", borderBottom: `2.5px solid ${filter === f.id ? T.primary : "transparent"}`, padding: "10px 14px", fontWeight: filter === f.id ? 700 : 500, fontSize: "0.82rem", color: filter === f.id ? T.primary : T.textSub, cursor: "pointer", transition: "all .2s", fontFamily: "inherit", whiteSpace: "nowrap" }}>
              {f.label}
              {f.id !== "all" && (
                <span style={{ marginLeft: 5, background: filter === f.id ? T.primary : T.border, color: filter === f.id ? "#fff" : T.textSub, fontSize: "0.62rem", fontWeight: 700, padding: "1px 6px", borderRadius: 100 }}>
                  {orders.filter(o => f.id === "all" || o.status === f.id).length}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Body */}
      <div style={{ maxWidth: 900, margin: "0 auto", padding: `14px ${px}px` }}>

        {/* Live tracking cards — always on top */}
        {filter !== "cancelled" && filter !== "delivered" && liveOrders.map(order => (
          <LiveTrackingCard
            key={order.id}
            orderId={order.id}
            initialOrder={order._raw}
            token={token}
          />
        ))}

        {/* Orders list */}
        {filteredOrders.length === 0 ? (
          <div style={{ textAlign: "center", padding: "60px 20px" }}>
            <Package size={52} color={T.border} strokeWidth={1} style={{ margin: "0 auto 16px", display: "block" }} />
            <div style={{ fontWeight: 700, fontSize: "0.97rem", color: T.textSub, marginBottom: 6 }}>No orders found</div>
            <div style={{ fontSize: "0.8rem", color: T.textMuted }}>
              {search ? "Try a different search term" : "Your orders will appear here"}
            </div>
          </div>
        ) : (
          <>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
              <div style={{ fontFamily: "'Fraunces',Georgia,serif", fontWeight: 800, fontSize: "0.97rem", letterSpacing: "-0.3px" }}>
                {filter === "all" ? "All Orders" : FILTERS.find(f => f.id === filter)?.label}
              </div>
              <div style={{ fontSize: "0.72rem", color: T.textSub }}>
                {filteredOrders.length} order{filteredOrders.length > 1 ? "s" : ""}
              </div>
            </div>
            {filteredOrders.map((order, i) => (
              <div key={order.id} style={{ animation: `fadeUp .38s ${i * 0.05}s ease both` }}>
                <OrderCard order={order} onClick={() => setDetail(order)} isMobile={isMobile} />
              </div>
            ))}
          </>
        )}
      </div>

      {/* Detail sheet */}
      {detailOrder && (
        <OrderDetailSheet order={detailOrder} onClose={() => setDetail(null)} isMobile={isMobile} token={token} />
      )}
    </div>
  );
}